"use client";

import { X, Play, MessageSquare } from "lucide-react";

// Derive up to 2-character initials from a track title / subtitle
function getInitials(title: string, subtitle?: string): string {
  const words = title.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words[0].length >= 2) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + (subtitle?.trim()[0] || '')).toUpperCase();
}

// Stable hue 0-360 from a string so each favorite gets a consistent colour
function stringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}
import { useAuth } from "@/contexts/auth-context";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { useVisibility } from "@/contexts/visibility-context";
import { subscribeFavorites, removeFavorite, reorderFavorites, type FavoriteItem } from "@/services/favorites";
import { batchGetUserImages } from "@/services/admin";
import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { db } from "@/lib/firebase";
import { collection, doc, getDoc, getDocs, query, where, documentId } from "firebase/firestore";
import { HeaderThumbnailSkeleton } from './header-thumbnail-skeleton';

export function FavoritesBar() {
  const { user } = useAuth();
  const { playTrack } = useMediaPlayer();
  const { isPageVisible } = useVisibility();
  // Match the app menu button and header height on every screen size.
  const thumbClassName = "bb-header-thumbnail size-[var(--header-height)] flex-shrink-0";
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [readyForUser, setReadyForUser] = useState<string | null>(null);
  const [liveUserImages, setLiveUserImages] = useState<Record<string, string>>({});
  // Favorites store a thumbnail SNAPSHOT from favorite-time (favorites.ts
  // addFavorite) that nothing patches when the source item's cover changes
  // later — editing a track/artist/release/video's thumbnail left the header
  // showing the old one until you unfavorited and re-favorited (Gordon:
  // "make sure... the favorites in the header automatically updates").
  // Rather than patching every thumbnail-edit call site to also write to
  // matching favorites docs (fragile — easy to forget on the next one), this
  // always live-joins the CURRENT thumbnail from the source collection on
  // every favorites snapshot, keyed by itemId (Firestore auto-ids are unique
  // across collections in practice, same assumption the old code already made).
  const [liveThumbImages, setLiveThumbImages] = useState<Record<string, string>>({});
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!user?.uid) {
      setFavorites([]);
      return;
    }
    const unsub = subscribeFavorites(user.uid, (favs) => {
      setFavorites(favs);
      setReadyForUser(user.uid);

      // Refresh live profile images for user-type favorites
      const userIds = favs
        .filter(f => f.itemType === 'user')
        .map(f => f.itemId)
        .filter(Boolean);
      if (userIds.length > 0) {
        batchGetUserImages(userIds).then(map => {
          setLiveUserImages(prev => ({ ...prev, ...map }));
        }).catch(() => {});
      }

      // Live-join the CURRENT thumbnail for every non-user favorite — always,
      // not just when the snapshot's own imageUrl is empty, so an edited
      // cover shows up here without needing to re-favorite. One batched `in`
      // query per source collection (Firestore caps `in` at 30 ids/query).
      const idsByCollection: Record<string, string[]> = { works: [], releases: [], artists: [], videos: [] };
      for (const f of favs) {
        if (!f.itemId) continue;
        if (f.itemType === 'track') idsByCollection.works.push(f.itemId);
        else if (f.itemType === 'release') idsByCollection.releases.push(f.itemId);
        else if (f.itemType === 'artist') idsByCollection.artists.push(f.itemId);
        else if (f.itemType === 'video') idsByCollection.videos.push(f.itemId);
      }
      const collectionsWithIds = Object.entries(idsByCollection).filter(([, ids]) => ids.length > 0);
      if (collectionsWithIds.length > 0) {
        (async () => {
          try {
            const BATCH = 30;
            const images: Record<string, string> = {};
            await Promise.all(
              collectionsWithIds.flatMap(([colName, ids]) =>
                Array.from({ length: Math.ceil(ids.length / BATCH) }, (_, i) =>
                  ids.slice(i * BATCH, i * BATCH + BATCH)
                ).map(async (batch) => {
                  const snap = await getDocs(
                    query(collection(db, colName), where(documentId(), 'in', batch))
                  );
                  snap.docs.forEach(d => {
                    const data = d.data();
                    const img = colName === 'videos'
                      ? (data.thumbnailUrl || data.imageUrl || '')
                      : (data.coverArtUrl || data.imageUrl || '');
                    if (img) images[d.id] = img;
                  });
                })
              )
            );
            if (Object.keys(images).length > 0) {
              setLiveThumbImages(prev => ({ ...prev, ...images }));
            }
          } catch { /* non-fatal — falls back to the (possibly stale) snapshot imageUrl */ }
        })();
      }
    });
    return () => unsub();
  }, [user?.uid]);

  // Live-refresh the instant a thumbnail is saved elsewhere in THIS session
  // (bb:thumbnail-updated, dispatched by updateWork/updateArtist/updateRelease/
  // updateVideo) — without this, a stale header thumbnail only self-corrects
  // on the next add/remove/reorder of a favorite, not the moment you edit the
  // cover. Cross-tab/cross-device edits still need the visit above to pick up.
  const favoritesRef = useRef<FavoriteItem[]>([]);
  favoritesRef.current = favorites;
  useEffect(() => {
    const THUMB_COLLECTION: Record<string, string> = { track: 'works', release: 'releases', artist: 'artists', video: 'videos' };
    const onThumbUpdated = (e: Event) => {
      const { itemType, itemId } = (e as CustomEvent).detail || {};
      const colName = THUMB_COLLECTION[itemType];
      if (!colName || !itemId) return;
      if (!favoritesRef.current.some(f => f.itemType === itemType && f.itemId === itemId)) return;
      (async () => {
        try {
          const snap = await getDoc(doc(db, colName, itemId));
          if (!snap.exists()) return;
          const data = snap.data();
          const img = itemType === 'video' ? (data.thumbnailUrl || data.imageUrl || '') : (data.coverArtUrl || data.imageUrl || '');
          if (img) setLiveThumbImages(prev => ({ ...prev, [itemId]: img }));
        } catch { /* non-fatal */ }
      })();
    };
    window.addEventListener('bb:thumbnail-updated', onThumbUpdated);
    return () => window.removeEventListener('bb:thumbnail-updated', onThumbUpdated);
  }, []);

  // Determine which page types are currently visible to this user
  const marketplaceVisible = isPageVisible('marketplace');

  // Filter out:
  //  • admin-hidden items (adminHidden flag on the favorite doc)
  //  • ALL marketplace favorites (itemType === 'item') — always hidden from header
  const visibleFavorites = favorites.filter(f => {
    if ((f as any).adminHidden) return false;
    if (f.itemType === 'item') return false;
    return true;
  // Cap at 40 (matches the logged-out header strip): an uncapped strip
  // renders every favorite as a thumbnail on EVERY page — real DOM/memory
  // weight on iPhone, where the renderer gets killed under memory pressure.
  }).slice(0, 40);

  const handleClick = (fav: FavoriteItem) => {
    if (fav.itemType === 'chat') {
      // Open chat window to this room
      window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId: fav.itemId } }));
      return;
    }
    if (fav.youtubeId) {
      playTrack({
        id: fav.itemId,
        title: fav.title,
        artist: fav.subtitle || "",
        imageUrl: fav.imageUrl || "",
        youtubeId: fav.youtubeId,
        mediaType: "youtube",
      });
    } else if (fav.audioUrl) {
      playTrack({
        id: fav.itemId,
        title: fav.title,
        artist: fav.subtitle || "",
        imageUrl: fav.imageUrl || "",
        audioUrl: fav.audioUrl,
        mediaType: "audio",
      });
    }
  };

  const handleRemove = async (e: React.MouseEvent, favId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await removeFavorite(favId);
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  // --- Drag and drop handlers ---
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Use a transparent image as drag ghost (we show our own indicator)
    const img = new window.Image();
    img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    e.dataTransfer.setDragImage(img, 0, 0);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverIndex(index);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    setOverIndex(index);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setOverIndex(null);
      dragCounter.current = 0;
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    dragCounter.current = 0;
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    // Reorder locally (operate on visible favorites only)
    const newFavs = [...visibleFavorites];
    const [moved] = newFavs.splice(dragIndex, 1);
    newFavs.splice(dropIndex, 0, moved);
    setFavorites(prev => {
      // Replace visible items in-place, keep hidden items
      const hidden = prev.filter(f => (f as any).adminHidden);
      return [...newFavs, ...hidden];
    });
    setDragIndex(null);
    setOverIndex(null);

    // Persist to Firestore
    try {
      await reorderFavorites(newFavs.map(f => f.id));
    } catch (err) {
      console.error("Error persisting reorder:", err);
    }
  }, [dragIndex, favorites]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    dragCounter.current = 0;
  }, []);

  // --- Touch drag handlers for mobile ---
  const touchState = useRef<{
    startX: number;
    startIndex: number;
    currentIndex: number;
    el: HTMLElement | null;
    clone: HTMLElement | null;
    items: HTMLElement[];
  } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent, index: number) => {
    const touch = e.touches[0];
    const el = (e.currentTarget as HTMLElement);

    // Create a clone for dragging visual
    const clone = el.cloneNode(true) as HTMLElement;
    clone.style.position = "fixed";
    clone.style.zIndex = "9999";
    clone.style.opacity = "0.85";
    clone.style.pointerEvents = "none";
    clone.style.width = "50px";
    clone.style.height = "50px";
    clone.style.left = `${touch.clientX - 25}px`;
    clone.style.top = `${touch.clientY - 25}px`;
    clone.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
    clone.style.borderRadius = "4px";
    document.body.appendChild(clone);

    const parent = el.parentElement;
    const items = parent ? Array.from(parent.children) as HTMLElement[] : [];

    touchState.current = {
      startX: touch.clientX,
      startIndex: index,
      currentIndex: index,
      el,
      clone,
      items,
    };

    el.style.opacity = "0.3";
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchState.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const { clone, items, startIndex } = touchState.current;

    if (clone) {
      clone.style.left = `${touch.clientX - 25}px`;
      clone.style.top = `${touch.clientY - 25}px`;
    }

    // Determine which item we're over
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      if (touch.clientX >= rect.left && touch.clientX <= rect.right) {
        touchState.current.currentIndex = i;
        // Visual feedback
        items.forEach((item, idx) => {
          if (idx === startIndex) {
            item.style.opacity = "0.3";
          } else if (idx === i && i !== startIndex) {
            item.style.transform = i < startIndex ? "translateX(50px)" : "translateX(-50px)";
            item.style.transition = "transform 0.15s ease";
          } else {
            item.style.transform = "";
            item.style.transition = "";
          }
        });
        break;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (!touchState.current) return;
    const { startIndex, currentIndex, el, clone, items } = touchState.current;

    // Clean up visuals
    if (clone) clone.remove();
    items.forEach(item => {
      item.style.opacity = "";
      item.style.transform = "";
      item.style.transition = "";
    });
    if (el) el.style.opacity = "";

    touchState.current = null;

    if (startIndex === currentIndex) return;

    // Reorder (operate on visible favorites only)
    const newFavs = [...visibleFavorites];
    const [moved] = newFavs.splice(startIndex, 1);
    newFavs.splice(currentIndex, 0, moved);
    setFavorites(prev => {
      const hidden = prev.filter(f => (f as any).adminHidden);
      return [...newFavs, ...hidden];
    });

    try {
      await reorderFavorites(newFavs.map(f => f.id));
    } catch (err) {
      console.error("Error persisting reorder:", err);
    }
  }, [favorites]);

  if (user?.uid && readyForUser !== user.uid) return <HeaderThumbnailSkeleton />;

  if (visibleFavorites.length === 0) {
    return (
      <div className="flex items-center h-full px-2">
        <span className="text-xs text-muted-foreground/50 select-none whitespace-nowrap">
          Click ♥ on tracks to pin them here
        </span>
      </div>
    );
  }

  // Compute display order for drag preview (use visibleFavorites)
  const displayFavs = (() => {
    if (dragIndex === null || overIndex === null || dragIndex === overIndex) return visibleFavorites;
    const result = [...visibleFavorites];
    const [moved] = result.splice(dragIndex, 1);
    result.splice(overIndex, 0, moved);
    return result;
  })();

  return (
    <div className="flex h-full items-stretch gap-px">
      {displayFavs.map((fav, index) => {
        const isChat = fav.itemType === 'chat';
        const isTrack = !isChat && !!(fav.audioUrl || fav.youtubeId || fav.videoUrl);
        // Prefer live-fetched images over the (possibly stale) snapshot copy
        // saved on the favorite doc at favorite-time.
        const displayImageUrl =
          fav.itemType === 'user'
            ? (liveUserImages[fav.itemId] || fav.imageUrl || '')
            : (liveThumbImages[fav.itemId] || fav.imageUrl || '');
        const linkHref = (!isChat && fav.link) ? fav.link : "#";
        const isDragging = dragIndex !== null && overIndex !== null && fav.id === visibleFavorites[dragIndex]?.id;
        const isClickable = isTrack || isChat;

        const thumb = (
          <div
            className={`relative ${thumbClassName} overflow-hidden group cursor-grab active:cursor-grabbing transition-transform duration-150 ${
              isDragging ? "opacity-50" : ""
            }`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, index)}
            onTouchMove={(e) => handleTouchMove(e)}
            onTouchEnd={handleTouchEnd}
            onClick={isClickable && dragIndex === null ? () => handleClick(fav) : undefined}
            title={`${fav.title}${fav.subtitle ? ` — ${fav.subtitle}` : ""}`}
          >
            {isChat ? (
              /* Chat group favorite: show initials + chat icon */
              (() => {
                const hue = stringToHue(fav.title);
                const initials = getInitials(fav.title);
                return (
                  <div
                    className="w-full h-full flex flex-col items-center justify-center select-none gap-0.5"
                    style={{ background: `hsl(${hue},40%,22%)` }}
                  >
                    <MessageSquare className="h-4 w-4 pointer-events-none" style={{ color: `hsl(${hue},70%,70%)` }} />
                    <span
                      className="font-bold leading-none pointer-events-none"
                      style={{ color: `hsl(${hue},80%,80%)`, fontSize: '0.55rem' }}
                    >
                      {initials}
                    </span>
                  </div>
                );
              })()
            ) : displayImageUrl ? (
              <Image
                src={displayImageUrl}
                alt={fav.title}
                fill
                className="object-cover pointer-events-none"
                sizes="(max-width: 389px) 44px, 50px"
                draggable={false}
              />
            ) : (
              (() => {
                const hue = stringToHue(fav.title + (fav.subtitle || ''));
                const initials = getInitials(fav.title, fav.subtitle);
                return (
                  <div
                    className="w-full h-full flex items-center justify-center select-none"
                    style={{ background: `hsl(${hue},45%,28%)` }}
                  >
                    <span
                      className="font-bold leading-none pointer-events-none"
                      style={{ color: `hsl(${hue},80%,88%)`, fontSize: '0.6rem' }}
                    >
                      {initials}
                    </span>
                  </div>
                );
              })()
            )}
            {isTrack && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <Play className="h-3.5 w-3.5 text-white fill-white" />
              </div>
            )}
            {isChat && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <MessageSquare className="h-3.5 w-3.5 text-white" />
              </div>
            )}
            <div
              className="absolute top-0 right-0 bg-destructive rounded-full p-0.5 bb-hover-reveal transition-opacity cursor-pointer z-10"
              onClick={(e) => handleRemove(e, fav.id)}
            >
              <X className="h-2.5 w-2.5 text-destructive-foreground" />
            </div>
          </div>
        );

        // For non-track, non-chat items, wrap in Link but prevent drag from triggering navigation
        if (!isTrack && !isChat && linkHref !== "#") {
          return (
            <div key={fav.id} className={thumbClassName}>
              {dragIndex !== null ? thumb : (
                <Link href={linkHref} className="block size-full" draggable={false}>
                  {thumb}
                </Link>
              )}
            </div>
          );
        }
        return <div key={fav.id} className={thumbClassName}>{thumb}</div>;
      })}
    </div>
  );
}
